import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  Tooltip,
  Link,
} from '@mui/material';
import { Stack } from '@mui/system';
import * as RouterPath from '../data/RouterPath';
import { useLocation, useNavigate } from 'react-router-dom';
import DialogType1 from '../components/DialogType1';
import { useLanguageStore } from '../store/useLanguageStore';
import { useRegionStore } from '../store/useRegionStore';
import useTokens from '../hooks/useTokens';
import useBaseUrl from '../hooks/useBaseUrl';
import { getCookie } from '../utils/cookie';
import { format } from 'date-fns-tz';

interface PatProps {
  created_at: number;
  id: string;
  name: string;
  revoked: boolean;
  hidden: boolean;
  scopes: string[];
}
interface LocationState {
  type?: 'login';
  emp?: {
    access_token?: string;
  };
}

function Tokens() {
  const navigate = useNavigate();
  // state가 null일 수 있으므로 안전하게 타입 지정
  const { state } = useLocation() as { state: LocationState | null };
  // 안전 분해: null인 경우 빈 객체로 대체
  const loginType = state?.type;
  const accessTokenFromState =
    loginType === 'login' ? state?.emp?.access_token : undefined;
  const [selectedToken, setSelectedToken] = useState<PatProps>({
    name: '',
    created_at: 0,
    id: '',
    hidden: false,
    revoked: false,
    scopes: [],
  });
  const [isRevoked, setIsRevoked] = useState(false);
  const [revokedToken, setRevokedToken] = useState('');
  const [tootipText, setTootipText] = useState('Copy to Clipboard');
  const [open, setOpen] = useState(false);
  const region = useRegionStore((s) => s.region);
  const baseUrl = useBaseUrl(region);
  const bearerToken = getCookie('bearer_token');
  const effectiveToken =
    state?.type === 'login' && state.emp?.access_token
      ? state.emp.access_token
      : bearerToken;

  const { pats, tokenState, setTokenState, revokeToken } = useTokens(
    baseUrl as string,
    false,
    effectiveToken
  );

  const { languagePack } = useLanguageStore();

  useEffect(() => {
    if (!effectiveToken) {
      navigate(RouterPath.LOGIN);
    }
  }, [effectiveToken, navigate]);

  // DialogType1
  const handleClickOpen = useCallback(
    (item: PatProps) => () => {
      setOpen(true);
      setSelectedToken(item);
    },
    []
  );
  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  // const revokeToken = () => {
  //   axios
  //     .delete(`${baseUrl}/pat/${selectedToken.id}`, { headers })
  //     .then((res) => {
  //       // initData();
  //     });
  //   setTokenState('');
  //   setIsRevoked(true);
  //   setRevokedToken(selectedToken.name);
  //   handleClose();
  // };

  return (
    <Box className="contents">
      <Box className="contents-header">
        <Typography className="title" variant="h3">
          {languagePack ? languagePack.tokens.title : ''}
        </Typography>
        <Stack direction="row">
          <Typography className="desc" variant="body1">
            {languagePack && languagePack.tokens.description1}
            <br />
            {languagePack && languagePack.tokens.description2}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => {
              navigate(RouterPath.TOKENS_NEW);
            }}
          >
            {languagePack && languagePack.tokens.addBtn}
          </Button>
        </Stack>
        <Typography
          sx={{
            fontSize: 12,
            color: '#e67777',
            paddingY: 2,
            maxWidth: '860px',
          }}
          variant="body1"
        >
          {languagePack && languagePack.tokens.caution}
        </Typography>
      </Box>
      {isRevoked ? (
        <>
          <Box className="contents-alert">
            <Alert severity="success" variant="filled">
              <span>{revokedToken}</span>{' '}
              {languagePack && languagePack.tokens.deleteFinished}
            </Alert>
          </Box>
        </>
      ) : null}
      {pats.length <= 0 ? (
        <>
          <Typography
            className="title"
            variant="h5"
            sx={{ margin: '2rem 3rem' }}
          >
            {languagePack && languagePack.tokens.haveNoToken}
          </Typography>
        </>
      ) : null}
      {tokenState === 'created' ? (
        <>
          <div>{tokenState}</div>
          <Box className="contents-alert">
            <div className="custom-info">
              {languagePack && languagePack.tokens.copyTokenMsg}
            </div>
          </Box>
          <Box className="contents-alert">
            <Paper className="contents-summary" sx={{ marginBottom: 5 }}>
              <Stack spacing={1} direction="row" className="copy">
                <Typography className="token" variant="h5">
                  {state?.result}
                </Typography>
                <Tooltip title={tootipText} arrow placement="right" id="copied">
                  <Link
                    className="copy-link"
                    onClick={() => {
                      navigator.clipboard.writeText(state?.result);
                      setTootipText('Copied!');
                    }}
                  >
                    {languagePack && languagePack.tokens.copyTokenBtn}
                  </Link>
                </Tooltip>
              </Stack>
            </Paper>
          </Box>
        </>
      ) : null}
      {tokenState === 'failed' ? (
        <>
          <Box className="contents-alert">
            <div className="custom-info custom-caution">
              {languagePack && languagePack.tokens.cannotAddToken}
            </div>
          </Box>
        </>
      ) : null}
      {pats &&
        pats
          .filter((item) => !item.hidden)
          .map((item, id) => {
            const date = new Date(item.created_at * 1000);
            const timeZone = 'Asia/Seoul';
            const displayDate = format(date, 'yyyy/MM/dd HH:mm zzz', {
              timeZone,
            });

            return (
              <Paper
                sx={{ display: 'flex', gap: 2 }}
                className="contents-summary"
                key={id}
              >
                <Stack spacing={1} direction="column">
                  <Typography className="title" variant="h5">
                    {item.name}
                  </Typography>
                  <Typography
                    sx={{ width: '100%', wordBreak: 'break-all' }}
                    className="desc"
                    variant="caption"
                  >
                    Token : {item.id}
                  </Typography>
                  <Typography className="desc" variant="caption">
                    Revoked : {item.revoked.toString()}
                  </Typography>
                  <Typography
                    sx={{ width: '100%', wordBreak: 'break-all' }}
                    className="desc"
                    variant="caption"
                  >
                    Created at : {displayDate}
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    flexDirection: { xs: 'column', sm: 'row' },
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                    }}
                    onClick={() => {
                      navigator.clipboard.writeText(item.id);
                      // setTootipText('Copied!');
                      alert(
                        languagePack ? languagePack.tokens.copied : 'Copied!'
                      );
                    }}
                  >
                    {languagePack && languagePack.tokens.copyToClipboard}
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    sx={{
                      margin: '0px !important',
                      width: { xs: '100%', sm: 'auto' },
                    }}
                    onClick={handleClickOpen(item)}
                  >
                    {languagePack && languagePack.tokens.deleteBtn}
                  </Button>
                </Box>
              </Paper>
            );
          })}

      {/* Dialog */}
      <DialogType1
        open={open}
        onClose={handleClose}
        token={selectedToken}
        revokeToken={() =>
          revokeToken(selectedToken, setIsRevoked, setRevokedToken, handleClose)
        }
      />
    </Box>
  );
}

export default Tokens;
